'use client';

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Zap, ArrowRight, Mail, Lock, AlertCircle, Eye, EyeOff, Loader2, KeyRound } from 'lucide-react';
import { formatAuthError, type FormattedError } from '../lib/errorHandler';

export function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorObj, setErrorObj] = useState<FormattedError | null>(null);
    const [resetSent, setResetSent] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const { login, resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorObj(null);
        setResetSent(false);
        setIsLoading(true);

        try {
            await login({ username: email.trim(), password });
            navigate('/');
        } catch (error) {
            console.error('Login error:', error);
            const formatted = formatAuthError(error);
            setErrorObj(formatted);
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email.trim()) {
            setErrorObj({
                title: 'Email Required',
                message: 'Please enter your email address above first, then click Forgot Password.'
            });
            return;
        }

        setIsResetting(true);
        setErrorObj(null);
        try {
            await resetPassword(email.trim());
            setResetSent(true);
        } catch (err) {
            const formatted = formatAuthError(err);
            setErrorObj(formatted);
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-card flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                {/* Logo */}
                <Link to="/" className="flex justify-center items-center gap-2 mb-8 group">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary shadow-md group-hover:scale-105 transition-transform">
                        <Zap className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <span className="text-2xl font-extrabold text-foreground tracking-tight">BgRemover</span>
                </Link>

                {/* Heading */}
                <h2 className="text-3xl font-bold text-foreground text-center mb-2">
                    Welcome back
                </h2>
                <p className="text-center text-muted-foreground mb-8 text-sm">
                    Sign in to your account to continue removing backgrounds
                </p>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-card border border-border rounded-2xl shadow-xl p-8 backdrop-blur-sm">
                    {/* User-Friendly Error Banner */}
                    {errorObj && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-sm">
                                <h4 className="font-semibold text-red-600 dark:text-red-400">
                                    {errorObj.title}
                                </h4>
                                <p className="text-muted-foreground mt-0.5 leading-relaxed">
                                    {errorObj.message}
                                </p>
                                {errorObj.suggestion && (
                                    <p className="text-xs text-muted-foreground mt-1.5 font-medium">
                                        💡 {errorObj.suggestion}
                                    </p>
                                )}
                                {errorObj.actionLink && errorObj.actionLabel && (
                                    <Link
                                        to={errorObj.actionLink}
                                        className="inline-block mt-2 text-xs font-semibold text-primary hover:underline"
                                    >
                                        {errorObj.actionLabel} &rarr;
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Password Reset Confirmation Banner */}
                    {resetSent && (
                        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-start gap-3 animate-in fade-in">
                            <KeyRound className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <h4 className="font-semibold text-green-600 dark:text-green-400">
                                    Password Reset Email Sent
                                </h4>
                                <p className="text-muted-foreground mt-0.5 text-xs">
                                    We sent a reset link to <strong className="text-foreground">{email}</strong>. Please check your inbox and spam folder.
                                </p>
                            </div>
                        </div>
                    )}

                    <form className="space-y-5" onSubmit={handleSubmit}>
                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (errorObj) setErrorObj(null);
                                    }}
                                    placeholder="you@example.com"
                                    className="block w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                                    Password
                                </label>
                                <button
                                    type="button"
                                    onClick={handleForgotPassword}
                                    disabled={isResetting}
                                    className="text-xs font-medium text-primary hover:text-primary/80 transition-colors hover:underline disabled:opacity-50"
                                >
                                    {isResetting ? 'Sending link...' : 'Forgot password?'}
                                </button>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (errorObj) setErrorObj(null);
                                    }}
                                    placeholder="••••••••"
                                    className="block w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-70 transition-all text-sm shadow-md mt-6"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Sign Up Link */}
                    <div className="mt-6 pt-6 border-t border-border text-center">
                        <p className="text-sm text-muted-foreground">
                            Don't have an account?{' '}
                            <Link to="/signup" className="font-semibold text-primary hover:text-primary/80 transition-colors">
                                Create one now
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
